import { z } from 'zod';
import { PostHogManager } from './manager.js';
import { POSTHOG_EXCEPTION_PROPERTY_NAMES } from './event-names.js';
import {
  COMMON_PROPERTIES,
  ENVELOPE_PROCESS_TYPE,
  EVENT_CATEGORIES,
  EVENT_NAMES
} from '#~/analytics/constants/index.js';
// device-catalog.js and room/utils.js rather than the device-context.js and
// room/index.js barrels: those two reach a React component and therefore the
// CJS-only UI deps that jest's --experimental-vm-modules linker cannot link.
// This module is re-exported by '#~/posthog/index.js', so anything it imports
// lands in the module graph of nearly every frontend suite.
import { resolveDeviceAnalyticsCatalogContext } from '#~/analytics/device-catalog.js';
import { locateDevice } from '#~/room/utils.js';
import { logger } from '#~/logger/index.js';
import { castId } from '@studio/models';
import type { Model } from '@studio/models/device';
import type { Device } from '#~/device/index.js';

// Legacy event name kept alive through the ENG-4342 backward-compat window so
// existing encoder-error dashboards/alerts do not break at cutover.
export const ENCODER_POSTHOG_EVENT = 'error_tracking.encoder_error';

// Canonical successor of ENCODER_POSTHOG_EVENT (ENG-4342). Dropped-dot, verb-noun
// shape to match the boot_*/download_* rename family. Registered canonical in
// analytics/canonical-event-map.json with a dual_write to the legacy name; the
// send-boundary receipt (studio/electron/e2e/tests/posthog-encoder-crash.spec.ts)
// asserts both fire once per crash during the window.
export const ENCODER_CRASH_EVENT = 'encoder_crash';

// See knowledge: newts-livekit-not-nats (ai.knowledge/platform/encoder) — Newts rides LiveKit.
const MAX_ENCODER_PAYLOAD_BYTES = 32 * 1024;
const MAX_ENCODER_PROPERTY_COUNT = 16;
const MAX_ENCODER_PROPERTY_KEY_LENGTH = 64;
const MAX_ENCODER_PROPERTY_STRING_LENGTH = 512;
const MAX_ENCODER_FIELD_STRING_LENGTH = MAX_ENCODER_PAYLOAD_BYTES;

const ENCODER_PROPERTY_ALLOWLIST = new Set([
  'title',
  'subtitle',
  'rateLimit',
  'posthog_forward_error'
]);

const boundedString = (max = MAX_ENCODER_PROPERTY_STRING_LENGTH) => z.string().max(max);
const encoderPropertyValueSchema = z.union([
  z.string().max(MAX_ENCODER_PROPERTY_STRING_LENGTH),
  z.boolean(),
  z.number().refine(Number.isFinite),
  z.null()
]);

type EncoderPropertyValue = z.infer<typeof encoderPropertyValueSchema>;

const encoderPropertiesSchema = z.custom<Record<string, EncoderPropertyValue>>(
  (value): value is Record<string, EncoderPropertyValue> => {
    if (!isPlainRecord(value)) return false;

    const entries = Object.entries(value);
    if (entries.length > MAX_ENCODER_PROPERTY_COUNT) return false;

    return entries.every(
      ([key, propertyValue]) =>
        key.length <= MAX_ENCODER_PROPERTY_KEY_LENGTH &&
        encoderPropertyValueSchema.safeParse(propertyValue).success
    );
  },
  { message: 'Expected a bounded plain object of encoder properties' }
);

const encoderErrorContextSchema = z
  .object({
    encoder_agent_id: boundedString().optional(),
    encoder_agent_instance_id: boundedString().optional(),
    encoder_version: boundedString().optional(),
    encoder_environment: boundedString().optional(),
    driver_kind: boundedString().optional(),
    studio_id: boundedString().optional(),
    org_id: boundedString().optional(),
    // Older encoders may send these; global camera attribution is intentionally ignored below.
    camera_model: boundedString().optional(),
    camera_brand: boundedString().optional()
  })
  .strict();

const encoderPostHogEnvelopeSchema = z
  .object({
    event_id: boundedString().optional(),
    posthog_event: boundedString().optional(),
    exception_type: boundedString().optional(),
    exception_message: z.string().min(1).max(MAX_ENCODER_FIELD_STRING_LENGTH),
    stack_trace: z.string().max(MAX_ENCODER_PAYLOAD_BYTES).optional(),
    severity: boundedString().optional(),
    // The Go forwarder stamps process_type on the envelope. It MUST be .optional()
    // so this .strict() schema still parses pre-stamp envelopes that omit it —
    // otherwise safeParse fails and every encoder error is dropped (the same
    // silent-drop trap camera_model/camera_brand are optional for).
    process_type: boundedString().optional(),
    timestamp: boundedString().optional(),
    properties: encoderPropertiesSchema.optional(),
    encoder_context: encoderErrorContextSchema.optional()
  })
  .strict();

export type EncoderErrorContext = z.infer<typeof encoderErrorContextSchema>;
export type EncoderPostHogEnvelope = z.infer<typeof encoderPostHogEnvelopeSchema>;

export type StudioEncoderErrorContext = {
  agentId: string;
  deviceId?: string;
  // The catalog model for `deviceId`, read from the SAME agent state tree the caller took
  // `deviceId` out of. Optional so callers holding only a device id (the e2e injection
  // probe) keep working through the room-projection fallback below. See ENG-4445 there
  // for why the projection alone is not enough.
  deviceModelId?: Model['id'];
  studioId?: string;
  orgId?: string;
};

type CaptureManager = Pick<PostHogManager, 'capture'>;

/** The two-valued crash-class level projected onto the PostHog $exception_level. */
const ENCODER_EXCEPTION_LEVEL = {
  FATAL: 'fatal',
  ERROR: 'error'
} as const;
type EncoderExceptionLevel = (typeof ENCODER_EXCEPTION_LEVEL)[keyof typeof ENCODER_EXCEPTION_LEVEL];

// TS mirror of the Go forwarder's resolveEnvelopeSeverity and ENG-4320's
// resolveExceptionLevel: a crash-class encoder capture (envelope severity
// 'fatal') projects to $exception_level 'fatal', everything else stays 'error'.
// Kept identical to the Go side so a true encoder process exit grades the same in
// PostHog Issues no matter which process emitted it. A bounded union (never a raw
// string literal at the call site) per never-use-type-assertions.
// Per ENG-4327 + F-6/ENG-4320. Rule: reference-rules-in-comments (ai.knowledge/global).
export function resolveEncoderExceptionLevel(severity: string | undefined): EncoderExceptionLevel {
  return severity === ENCODER_EXCEPTION_LEVEL.FATAL
    ? ENCODER_EXCEPTION_LEVEL.FATAL
    : ENCODER_EXCEPTION_LEVEL.ERROR;
}

// Per rule: architecture-principles (ai.knowledge/global) — reuse the agent Newts channel.
export function encoderPostHogSubject(agentId: string): string {
  return `direct.authenticated.${agentId}.posthog.error`;
}

export function decodeEncoderPostHogEnvelope(data: Uint8Array): EncoderPostHogEnvelope | null {
  if (data.byteLength > MAX_ENCODER_PAYLOAD_BYTES) {
    return null;
  }

  try {
    const raw = new TextDecoder().decode(data);
    const parsed = JSON.parse(raw) as unknown;
    const result = encoderPostHogEnvelopeSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function captureEncoderPostHogEnvelope(
  envelope: EncoderPostHogEnvelope,
  context: StudioEncoderErrorContext,
  manager: CaptureManager = PostHogManager.getInstance()
): boolean {
  const result = encoderPostHogEnvelopeSchema.safeParse(envelope);
  if (!result.success) {
    return false;
  }

  const validEnvelope = result.data;
  const encoderContext = validEnvelope.encoder_context ?? {};
  const message = validEnvelope.exception_message;

  const groups = resolveEncoderGroups(encoderContext, context);
  const eventId = resolveEncoderEventId(validEnvelope, context);
  const safeEnvelopeProperties = pickSafeEnvelopeProperties(validEnvelope.properties);
  // PostHog custom events need explicit groups; autocapture enrichment does not run here.
  const properties: Record<string, unknown> = {
    ...safeEnvelopeProperties,
    // Stable insert IDs prevent Newts fan-out from counting one encoder error once per viewer.
    $insert_id: `encoder:${eventId}`,
    encoder_event_id: eventId,
    encoder_error_type: validEnvelope.exception_type || 'EncoderError',
    encoder_error_message: message,
    encoder_version: encoderContext.encoder_version ?? null,
    encoder_environment: encoderContext.encoder_environment ?? null,
    encoder_agent_id: encoderContext.encoder_agent_id ?? context.agentId,
    encoder_agent_instance_id: encoderContext.encoder_agent_instance_id ?? null,
    agent_id: context.agentId,
    studio_id: context.studioId ?? encoderContext.studio_id ?? null,
    org_id: context.orgId ?? encoderContext.org_id ?? null,
    driver_kind: encoderContext.driver_kind ?? null,
    stack_trace: validEnvelope.stack_trace ?? null,
    severity: validEnvelope.severity ?? null,
    // Project the envelope severity onto PostHog's canonical $exception_level so
    // Issues grades a true encoder process exit as crash-class (fatal), mirroring
    // the main-process F-6 path. Legacy `severity` above stays for existing
    // consumers (additive, no hard cutover).
    [POSTHOG_EXCEPTION_PROPERTY_NAMES.LEVEL]: resolveEncoderExceptionLevel(validEnvelope.severity),
    // Read the process_type the producer stamped rather than hard-coding one, so a
    // second producer on this channel reports itself instead of being mislabelled.
    // Pre-stamp encoders omit the field; ENCODER is the correct fallback because
    // the forwarder ships only in the encoder's own binaries.
    [COMMON_PROPERTIES.PROCESS_TYPE]: validEnvelope.process_type ?? ENVELOPE_PROCESS_TYPE.ENCODER,
    encoder_timestamp: validEnvelope.timestamp ?? null,
    $exception_source: 'encoder',
    $exception_component: 'encoder'
  };

  if (groups) {
    properties.$groups = groups;
  }

  // ENG-4342 rename dual-emit: fire the canonical successor AND the legacy name
  // once each per crash during the backward-compat window. Distinct $insert_ids
  // keep PostHog from cross-deduping the pair; the legacy keeps its exact
  // pre-rename insert id (`encoder:${eventId}`) so legacy dashboards are byte-
  // for-byte unchanged. Each manager.capture is one physical send — there is no
  // debounce on this path, so a crash fires each name exactly once, never twice.
  manager.capture(ENCODER_CRASH_EVENT, {
    ...properties,
    $insert_id: `encoder:${eventId}:${ENCODER_CRASH_EVENT}`
  });
  manager.capture(ENCODER_POSTHOG_EVENT, properties);
  captureCanonicalDeviceErrorEvents(validEnvelope, properties, context, manager);

  return true;
}

export function captureEncoderPostHogMessage(
  data: Uint8Array,
  context: StudioEncoderErrorContext,
  manager?: CaptureManager
): boolean {
  const envelope = decodeEncoderPostHogEnvelope(data);
  if (!envelope) {
    logger.warning('Unable to decode encoder PostHog error envelope.');
    return false;
  }

  if (!captureEncoderPostHogEnvelope(envelope, context, manager)) {
    logger.warning('Ignoring invalid encoder PostHog error envelope.');
    return false;
  }

  return true;
}

function resolveEncoderEventId(
  envelope: EncoderPostHogEnvelope,
  context: StudioEncoderErrorContext
): string {
  if (typeof envelope.event_id === 'string' && envelope.event_id.length > 0) {
    return envelope.event_id;
  }

  return stableHash(
    [
      context.agentId,
      envelope.exception_type ?? '',
      envelope.exception_message ?? '',
      envelope.timestamp ?? '',
      envelope.stack_trace ?? ''
    ].join('\u001f')
  );
}

function stableHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function resolveEncoderGroups(
  encoderContext: EncoderErrorContext,
  context: StudioEncoderErrorContext
): Record<string, string> | undefined {
  const groups: Record<string, string> = {};
  const orgId = context.orgId ?? encoderContext.org_id;
  const studioId = context.studioId ?? encoderContext.studio_id;

  if (orgId) {
    groups.org = orgId;
  }
  if (studioId) {
    groups.studio = studioId;
  }
  return Object.keys(groups).length > 0 ? groups : undefined;
}

function pickSafeEnvelopeProperties(
  properties: Record<string, EncoderPropertyValue> | undefined
): Record<string, EncoderPropertyValue> {
  const safeProperties: Record<string, EncoderPropertyValue> = {};
  if (!properties) return safeProperties;

  // Encoder-supplied properties cannot override reserved PostHog attribution.
  for (const key of ENCODER_PROPERTY_ALLOWLIST) {
    if (Object.prototype.hasOwnProperty.call(properties, key)) {
      const value = properties[key];
      if (value !== undefined) {
        safeProperties[key] = value;
      }
    }
  }
  return safeProperties;
}

function captureCanonicalDeviceErrorEvents(
  envelope: EncoderPostHogEnvelope,
  encoderProperties: Record<string, unknown>,
  context: StudioEncoderErrorContext,
  manager: CaptureManager
): void {
  // Canonical device funnels require a concrete device attribution.
  if (!context.deviceId) {
    return;
  }

  const eventId = String(
    encoderProperties.encoder_event_id ?? resolveEncoderEventId(envelope, context)
  );
  // This bypass surface builds its property literal by hand instead of routing
  // through a domain tracker (per ENG-3960), so brand/model attribution has to be
  // pulled from the same catalog resolver every tracker uses, or device_brand_id
  // and device_model_id stay null on every row this surface ever emits (ENG-4445).
  //
  // The model id comes from the caller when it has one, because the caller reads it out
  // of the same agent control tree it read `deviceId` out of, and those two are the pair
  // that always agree. `locateDevice` resolves against a DIFFERENT and narrower set —
  // depot rooms[*].devices — and that set is FILTERED, which is the actual reason the
  // lookup misses. pbToJsDevice (protobuf/device.ts:223) drops any device lacking a
  // committed model, a name, or all media: `if (!modelId || !name || (!audio && !control
  // && !video)) return undefined;`. The Add-Source wizard streams its preview before the
  // operator commits a model, so a preview device has no model and never enters the
  // projection at all — the lookup answers undefined for exactly the population this fix
  // exists to attribute, and undefined silently drops all four catalog properties through
  // the conditional spreads below.
  //
  // Do NOT restate this as a Room-mount lifecycle argument. Two earlier drafts did and both
  // halves are false: the unmount cleanup in room/provider/provider.tsx is dead code (an
  // unconditional `return;` precedes it at :246, so the projection survives unmount), and
  // an encoder error cannot arrive with no Room mounted because the subscription is created
  // in the ProxyImpl constructor, which is only constructed via agent/broker.ts:799 from the
  // Room provider. No Room ⇒ no proxy ⇒ no encoder error.
  //
  // Consequence worth knowing: the caller path and this fallback gate on the SAME field
  // (control.studio.device[id].model, which pbStudio.model feeds), so the fallback cannot
  // resolve anything the caller could not. It is retained only for callers that pass a
  // device id alone — the e2e injection probe — and is effectively dead for the production
  // caller.
  //
  // `??` (not `||`) here is a documented latent divergence: the guard below is a
  // truthiness test while this resolver arg is nullish, so on a '' model id the ternary
  // treats '' as absent and runs the lookup, then `??` keeps '' and discards that result,
  // yielding {} with all four catalog props dropped. Left as-is because '' is unreachable
  // in production — toHexFromId returns undefined for zero-length ids and proxy.ts
  // re-guards deviceModelId with a truthiness spread before it ever reaches here — and the
  // '' behaviour is pinned by a characterization test in
  // encoder-errors.device-catalog.test.ts so a change that makes '' reachable fails loudly.
  const projectedDevice = context.deviceModelId
    ? undefined
    : locateDevice(castId<Device['id']>(context.deviceId));
  const deviceCatalogContext = resolveDeviceAnalyticsCatalogContext(
    context.deviceModelId ?? projectedDevice?.modelId
  );
  const baseProperties = {
    $groups: encoderProperties.$groups,
    agent_id: context.agentId,
    device_id: context.deviceId,
    ...(context.studioId ? { studio_id: context.studioId } : {}),
    ...(context.orgId ? { org_id: context.orgId } : {}),
    ...(deviceCatalogContext?.brand_name ? { brand_name: deviceCatalogContext.brand_name } : {}),
    ...(deviceCatalogContext?.model_name ? { model_name: deviceCatalogContext.model_name } : {}),
    ...(deviceCatalogContext?.device_brand_id
      ? { device_brand_id: deviceCatalogContext.device_brand_id }
      : {}),
    ...(deviceCatalogContext?.device_model_id
      ? { device_model_id: deviceCatalogContext.device_model_id }
      : {}),
    category: EVENT_CATEGORIES.DEVICE,
    surface: 'encoder_error',
    is_error_state: true,
    error_type: 'encoder_error',
    error_message: envelope.exception_message,
    encoder_event_id: eventId,
    encoder_error_type: envelope.exception_type || 'EncoderError',
    driver_kind: envelope.encoder_context?.driver_kind ?? null,
    timestamp: Date.now()
  };

  // The per-name insert id keeps a retried encoder envelope idempotent in PostHog.
  const emitCanonical = (canonicalName: string, extraProperties: Record<string, unknown>): void => {
    manager.capture(canonicalName, {
      ...baseProperties,
      ...extraProperties,
      $insert_id: `encoder:${eventId}:${canonicalName}`
    });
  };

  // Per think-in-systems (ai.knowledge/global), retire this alias at the encoder bypass
  // sender too. A stable canonical insert id still deduplicates repeated Newts delivery.
  manager.capture(EVENT_NAMES.MEDIA_CONNECT_FAILED, {
    ...baseProperties,
    media_type: 'video',
    $insert_id: `encoder:${eventId}:${EVENT_NAMES.MEDIA_CONNECT_FAILED}`
  });
  emitCanonical(EVENT_NAMES.MEDIA_STREAM_FAILED, { media_type: 'video' });
  emitCanonical(EVENT_NAMES.CONTROLS_CONNECT_FAILED, {
    media_type: 'controls',
    prior_success: false
  });
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
